/**
 * @kgc/partners — ContactPerson read-seam DTO (GAP-4 read-oldal, nagykör-1, 2026-06-11)
 *
 * A `GET /partners-direct/:partnerId/contact-persons` wire-kontraktusa.
 * A Prisma `ContactPerson` modell (partner.prisma `contact_persons`) RÉG létezik,
 * de DTO/endpoint nélkül — a page9 kontakt-tab eddig a `Partner.contactName`-ből
 * szintetizált mock-listát kapott. Ez a seam a VALÓDI rekordokat adja.
 *
 * Pinnelt fixture: `__fixtures__/contact-person-list.contract-fixture.json`
 * (hash-pin guard a contact-person-read.contract.spec.ts-ben) — a FE MSW EZT szolgálja.
 *
 * ⚠️ Scope-határ (proto-03 Q-C005): ez a MEGLÉVŐ 1:N `ContactPerson` read-seamje,
 * NEM az M:N `PartnerRelationship` (az Phase-2-DEFER a 4 kliens-kérdés zárásáig).
 */
import { z } from 'zod';

/** ContactPerson domain-entitás (repository-réteg, Date-objektumokkal) */
export interface IContactPerson {
  /** Egyedi azonosító (UUID) */
  id: string;
  /** Tenant azonosító (multi-tenancy) */
  tenantId: string;
  /** Partner azonosító */
  partnerId: string;
  /** Kapcsolattartó neve */
  name: string;
  /** E-mail cím */
  email?: string | undefined;
  /** Telefonszám */
  phone?: string | undefined;
  /** Beosztás / szerep */
  position?: string | undefined;
  /** Elsődleges kapcsolattartó-e */
  isPrimary: boolean;
  /** Létrehozás dátuma */
  createdAt: Date;
  /** Módosítás dátuma */
  updatedAt: Date;
}

/** Wire-formátum (JSON: Date → ISO-8601 string) */
export const ContactPersonReadSchema = z.object({
  /** Egyedi azonosító (UUID) */
  id: z.string().uuid(),
  /** Tenant azonosító (multi-tenancy) */
  tenantId: z.string().uuid(),
  /** Partner azonosító */
  partnerId: z.string().uuid(),
  /** Kapcsolattartó neve */
  name: z.string(),
  /** E-mail cím */
  email: z.string().optional(),
  /** Telefonszám */
  phone: z.string().optional(),
  /** Beosztás / szerep */
  position: z.string().optional(),
  /** Elsődleges kapcsolattartó-e */
  isPrimary: z.boolean(),
  /** Létrehozás dátuma (wire: ISO-8601) */
  createdAt: z.string().datetime(),
  /** Módosítás dátuma (wire: ISO-8601) */
  updatedAt: z.string().datetime(),
});

export type ContactPersonReadDto = z.infer<typeof ContactPersonReadSchema>;

/**
 * `GET /partners-direct/:partnerId/contact-persons` teljes válasz-kontraktus:
 * `{ data: [...] }` wrapper (API-konvenció), isPrimary-first rendezés a BE-garancia.
 */
export const ContactPersonListResponseSchema = z.object({
  data: z.array(ContactPersonReadSchema),
});

export type ContactPersonListResponseDto = z.infer<typeof ContactPersonListResponseSchema>;
